import { GRAY_700, GRAY_800, GRAY_900, SELECTED_BLUE } from 'constants/styling/theme'
import { ImageNotSupportedOutlined, SmartDisplayOutlined } from '@mui/icons-material'
import { MediaFormat, SocialNetwork } from 'models/socialCampaign'

import { EditorMedia, isFeedTextCut, isInstagramCaptionCut, textRunsOf } from 'utils/socialCampaign/postEditor'
import { FC, Fragment } from 'react'
import { FeedEngagement } from './FeedEngagement.component'
import { MediaThumb } from 'components/common/MediaThumb'
import { NetworkMark } from './NetworkMark.component'
import { PreviewAccount } from './usePreviewAccounts'
import Typography from '@mui/material/Typography'
import { UserAvatarChip } from 'components/common/UserAvatarChip'
import classnames from 'classnames'
import igComment from 'assets/img/socialNetworks/reactions/ig-comment.svg'
import igFavorite from 'assets/img/socialNetworks/reactions/ig-favorite.svg'
import igLike from 'assets/img/socialNetworks/reactions/ig-like.svg'
import igRepost from 'assets/img/socialNetworks/reactions/ig-repost.svg'
import igShare from 'assets/img/socialNetworks/reactions/ig-share.svg'
import styles from './PostPreviewCards.module.sass'
import { useMeasuredCut } from './useMeasuredCut'
import { useTranslation } from 'react-i18next'

/*
 * The networks' cards (prototype v3 `igCard` / `feedCard` / the TikTok frame, ported from v1
 * `marketing-campaign-post.js`): they imitate the networks on purpose. The engagement counters are fixed
 * decorations, as in the prototype. Instagram's glyphs are v1's files, drawn as <img> (their ids would clash inlined).
 */

export interface PreviewCardProps {
  network: SocialNetwork
  /** Who posts; "Your <network> account" with the network's mark when unknown */
  account: PreviewAccount | null
  /** What the network publishes of the post's visuals, in order (`keptBy`) */
  kept: EditorMedia[]
  /** The text as it goes out, or the placeholder */
  text: string
}

const Glyph: FC<{ src: string, size?: number }> = ({ src, size = 20 }) => <img src={src} width={size} height={size} alt="" />

/** The post's text with its hashtags and @mentions in the network's link colour (`.tag` per card), as React children. */
const TaggedText: FC<{ text: string }> = ({ text }) => (
  <>
    {textRunsOf(text).map((run, index) => run.isTag
      ? <span key={index} className={styles.tag}>{run.text}</span>
      : <Fragment key={index}>{run.text}</Fragment>
    )}
  </>
)

/** The author's picture at the network's size (its initial without one), else the network's mark; the name follows. */
const Author: FC<{ network: SocialNetwork, account: PreviewAccount | null, size: number }> = ({ network, account, size }) => {
  const { t } = useTranslation(['social_campaign'])
  const name = account?.name ?? t('editor.preview.account', { network: t(`network.${network}`) })

  return (
    <header className={styles.head}>

      {account
        // decorative: the name is written next to it
        ? <span className={styles.avatar} aria-hidden="true"><UserAvatarChip isAvatarOnly name={account.name} avatarUrl={account.avatarUrl ?? undefined} avatarSize={size} /></span>
        : <NetworkMark network={network} size={size} />
      }

      <Typography variant="text-md" component="span" fontWeight={550} color={GRAY_900}>{name}</Typography>

    </header>
  )
}

/** A visual in a card (prototype `mediaHtml`): its thumbnail, a video's first frame with its pill, a document's PDF pill. */
export const PreviewVisual: FC<{ media: EditorMedia }> = ({ media }) => {
  const { t } = useTranslation(['social_campaign'])
  const video = media.format === MediaFormat.VIDEO
  const url = video ? media.previewUrl : media.thumbnailUrl ?? media.previewUrl

  return (
    <>

      {video
        ? <MediaThumb className={styles.thumb} kind="video" src={url ? `${url}#t=0.5` : undefined} alt="" kindIcon={<SmartDisplayOutlined />} />
        : (
          <MediaThumb
            className={styles.thumb}
            kind={media.format === MediaFormat.DOCUMENT ? 'pdf' : 'photo'}
            src={url ?? undefined}
            alt=""
            kindLabel={media.format === MediaFormat.DOCUMENT ? t('schedule.document') : undefined}
          />
        )
      }

      {/* a visual the viewer's seat does not show: the backend signs no URL for it, the post keeps it */}
      {!url &&
        <span className={styles.hidden} title={t('editor.visual_hidden')}><ImageNotSupportedOutlined /></span>
      }

    </>
  )
}

/**
 * A Facebook or LinkedIn text, four lines at most, with "… See more" over the end of the last when they cut it: as
 * measured where there is a layout, else by `isFeedTextCut`.
 */
const FeedText: FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation(['social_campaign'])
  const [lines, cuts] = useMeasuredCut<HTMLParagraphElement>(text, 'height')

  return (
    <div className={styles.textBox}>

      <Typography ref={lines} variant="text-sm" component="p" color={GRAY_800} className={styles.text}><TaggedText text={text} /></Typography>

      {(cuts ?? isFeedTextCut(text)) &&
        <Typography variant="text-sm" component="span" className={styles.seeMore} aria-hidden="true">{t('editor.preview.see_more')}</Typography>
      }

    </div>
  )
}

/** Facebook and LinkedIn: avatar, name, text, a grid of up to four visuals with "+n" on the last, the network's reactions and actions. */
export const FeedCard: FC<PreviewCardProps> = ({ network, account, kept, text }) => {
  const shots = kept.slice(0, 4)
  // past four visuals the last cell is covered by "+n", so it counts as hidden too
  const extra = kept.length > 4 ? kept.length - 3 : 0

  return (
    <article className={styles.card} data-network={network}>

      <Author network={network} account={account} size={50} />

      <FeedText text={text} />

      {shots.length > 0 &&
        <div className={styles.grid} data-count={shots.length}>
          {shots.map((media, index) => (
            <span key={index} className={styles.cell}>

              <PreviewVisual media={media} />

              {index === shots.length - 1 && extra > 0 && <span className={styles.more}>{`+${extra}`}</span>}

            </span>
          ))}
        </div>
      }

      <FeedEngagement network={network} />

    </article>
  )
}

/** Instagram's caption: its first line, then "more" when the feed cuts it (`isInstagramCaptionCut`) or the line overflows. */
const Caption: FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation(['social_campaign'])
  const [line, overflows] = useMeasuredCut<HTMLSpanElement>(text, 'width')

  return (
    <Typography variant="text-sm" component="p" color={GRAY_900} className={styles.caption}>

      <span ref={line} className={styles.captionText}><TaggedText text={text} /></span>

      {(isInstagramCaptionCut(text) || overflows === true) &&
        <span className={styles.moreLink} aria-hidden="true">{t('editor.preview.more')}</span>
      }

    </Typography>
  )
}

/**
 * Instagram: one square visual (9:16 for a Reel) with the carousel position and dots, the action row, the caption cut
 * behind "more".
 */
export const InstagramCard: FC<PreviewCardProps & { isReel?: boolean }> = ({ network, account, kept, text, isReel = false }) => {
  const { t } = useTranslation(['social_campaign'])
  const first = kept[0]

  return (
    <article className={styles.card} data-network={network}>

      <Author network={network} account={account} size={40} />

      <div className={classnames(styles.shot, { [styles.reel]: isReel })}>

        {first && <PreviewVisual media={first} />}

        {!first && <Typography variant="text-sm" component="span" color={GRAY_700}>{t('editor.preview.no_visual')}</Typography>}

        {kept.length > 1 && <span className={styles.position}>{`1/${kept.length}`}</span>}

      </div>

      {kept.length > 1 &&
        <div className={styles.dots} aria-hidden="true">
          {/* the one shown in SELECTED_BLUE (no sass variable for it) */}
          {kept.map((_, index) => <span key={index} className={styles.dot} data-on={index === 0} style={index === 0 ? { background: SELECTED_BLUE } : undefined} />)}
        </div>
      }

      <div className={styles.igActs} aria-hidden="true">

        <span className={styles.igLeft}>

          <span className={styles.igAct}><Glyph src={igLike} />8</span>

          <span className={styles.igAct}><Glyph src={igComment} />26</span>

          <Glyph src={igRepost} />

          <Glyph src={igShare} />

        </span>

        <Glyph src={igFavorite} />

      </div>

      <Caption text={text} />

      <Typography variant="text-sm" component="p" color={GRAY_700} className={styles.comments} aria-hidden="true">{t('editor.preview.comments', { number: 26 })}</Typography>

    </article>
  )
}

/**
 * TikTok: a 9:16 frame, the first visual full bleed (a video's first frame with a play glyph), its position, then the
 * account, a photo post's title and two lines of the text over a gradient.
 */
export const TikTokCard: FC<PreviewCardProps & { title: string }> = ({ network, account, kept, text, title }) => {
  const { t } = useTranslation(['social_campaign'])
  const first = kept[0]
  const video = first?.format === MediaFormat.VIDEO
  const url = first && (video ? first.previewUrl : first.thumbnailUrl ?? first.previewUrl)

  return (
    <article className={styles.tiktok} data-network={network}>
      <div className={styles.frame}>

        {!first && <Typography variant="text-sm" component="span" color={GRAY_700}>{t('editor.preview.no_visual')}</Typography>}

        {first && !url && <span className={styles.hidden} title={t('editor.visual_hidden')}><ImageNotSupportedOutlined /></span>}

        {url && video && <video src={`${url}#t=0.5`} muted playsInline preload="metadata" aria-hidden="true" />}

        {url && video && <span className={styles.play} aria-hidden="true">▶</span>}

        {url && !video && <img src={url} alt="" />}

        {kept.length > 1 && <span className={styles.count}>{`1/${kept.length}`}</span>}

        <div className={styles.overlay}>

          <strong>{account?.name ?? t('editor.preview.account', { network: t(`network.${network}`) })}</strong>

          {title && <span className={styles.title}>{title}</span>}

          <span className={styles.clip}><TaggedText text={text} /></span>

        </div>

      </div>
    </article>
  )
}
